import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { useWebsiteVisitsByMonthYtd } from '../../hooks/useWebsiteVisitsByMonthYtd';
import { conversionTypeCaption } from '../../lib/websiteVisitsGraph';
import { fetchWebsiteChart, type WebsiteReportChart, type WebsiteReportType } from '../../lib/websiteVisitsReport';
import { WebsiteConversionTypeTotals } from './WebsiteVisitsConversionSettingsPanel';
import WebsiteVisitsLineChart from './WebsiteVisitsLineChart';

function typeSummary(types: WebsiteReportType[]): string {
  return types
    .map((type) => `${type.count.toLocaleString()} ${conversionTypeCaption(type.label, type.count)}`)
    .join(' · ');
}

interface WebsiteVisitsByMonthYtdModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Year-to-date website visits by month; click a month for the daily visits report. */
export default function WebsiteVisitsByMonthYtdModal({
  isOpen,
  onClose,
}: WebsiteVisitsByMonthYtdModalProps) {
  const {
    months,
    yearVisits,
    yearUnique,
    yearConversionRate,
    yearConversionTypes,
    yearLabel,
    loading,
    error,
    load,
    businessId,
    timeZone,
  } = useWebsiteVisitsByMonthYtd();
  const [selectedMonthKey, setSelectedMonthKey] = useState<string | null>(null);
  const [visitsMode, setVisitsMode] = useState<'visits' | 'unique'>('visits');
  const [monthDetail, setMonthDetail] = useState<WebsiteReportChart | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedMonthKey(null);
      setMonthDetail(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedMonthKey) setSelectedMonthKey(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, selectedMonthKey]);

  const selectedMonth = useMemo(
    () => months.find((m) => m.monthKey === selectedMonthKey) ?? null,
    [months, selectedMonthKey],
  );

  useEffect(() => {
    if (!selectedMonth || !businessId) return;
    let cancelled = false;
    setDetailLoading(true);
    setMonthDetail(null);
    void fetchWebsiteChart(businessId, selectedMonth.since, selectedMonth.until, timeZone)
      .then((chart) => {
        if (!cancelled) setMonthDetail(chart);
      })
      .catch((err: unknown) => {
        console.error('[WebsiteVisitsByMonthYtdModal] month detail', err);
        if (!cancelled) setMonthDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMonth, businessId, timeZone]);

  if (!isOpen) return null;

  const inDetail = Boolean(selectedMonthKey);
  const detailTypes = (monthDetail?.types ?? selectedMonth?.conversionTypes ?? []).map((type) => ({
    id: type.label,
    label: type.label,
    count: type.count,
  }));
  const detailVisits = monthDetail?.visits ?? selectedMonth?.visits ?? 0;
  const detailUnique = monthDetail?.users ?? selectedMonth?.unique ?? 0;
  const detailRate = monthDetail?.conversionRate
    ?? (selectedMonth?.conversionRate != null ? selectedMonth.conversionRate.toFixed(1) : null);

  return createPortal(
    <div
      className="fixed inset-0 z-[99998] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="website-visits-by-month-ytd-title"
    >
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className={`relative w-full ${
          inDetail ? 'max-w-xl' : 'max-w-lg'
        } max-h-[min(85vh,560px)] flex flex-col rounded-2xl border border-gray-200/90 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] overflow-hidden`}
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
          <div className="flex min-w-0 items-start gap-2">
            {inDetail ? (
              <button
                type="button"
                onClick={() => setSelectedMonthKey(null)}
                className="mt-0.5 shrink-0 p-1 -ml-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                aria-label="Back to months"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            ) : null}
            <div className="min-w-0">
              <h2
                id="website-visits-by-month-ytd-title"
                className="text-base font-semibold text-gray-900"
              >
                {inDetail ? selectedMonth?.label ?? 'Website Visits' : 'Website Visits'}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {inDetail
                  ? 'Daily visits report'
                  : yearLabel
                    ? `Year to date · ${yearLabel}`
                    : 'Year to date'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100 shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            {inDetail ? selectedMonth?.label ?? 'Month' : 'Year to date'}
          </p>
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-gray-300 mt-1" />
          ) : inDetail ? (
            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
              <button
                type="button"
                onClick={() => setVisitsMode('visits')}
                className={`flex items-center gap-1.5 transition-opacity ${
                  visitsMode === 'visits' ? 'opacity-100' : 'opacity-40 hover:opacity-70'
                }`}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: 'rgb(59, 130, 246)' }}
                />
                <span className="text-2xl font-bold text-gray-900 tabular-nums">
                  {detailVisits.toLocaleString()}
                </span>
                <span className="text-[10px] text-gray-500 self-end mb-0.5">visits</span>
              </button>
              <button
                type="button"
                onClick={() => setVisitsMode('unique')}
                className={`flex items-center gap-1.5 transition-opacity ${
                  visitsMode === 'unique' ? 'opacity-100' : 'opacity-40 hover:opacity-70'
                }`}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                />
                <span className="text-2xl font-bold text-gray-900 tabular-nums">
                  {detailUnique.toLocaleString()}
                </span>
                <span className="text-[10px] text-gray-500 self-end mb-0.5">unique</span>
              </button>
              {detailRate ? (
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: 'rgb(234, 88, 12)' }}
                  />
                  <span className="text-2xl font-bold text-gray-900 tabular-nums">
                    {detailRate}%
                  </span>
                  <span className="text-[10px] text-gray-500 self-end mb-0.5">conv.</span>
                </div>
              ) : null}
              <WebsiteConversionTypeTotals types={detailTypes} />
            </div>
          ) : (
            <div className="mt-0.5">
              <p className="text-2xl font-bold text-gray-900 tabular-nums tracking-tight">
                {yearVisits.toLocaleString()}
                <span className="ml-2 text-sm font-medium text-gray-400">
                  visits · {yearUnique.toLocaleString()} unique
                </span>
              </p>
              {yearConversionRate != null ? (
                <p className="text-sm font-medium text-orange-600 mt-0.5 tabular-nums">
                  {yearConversionRate.toFixed(1)}% conversion
                </p>
              ) : null}
              {yearConversionTypes.length > 0 ? (
                <p className="text-sm font-medium text-emerald-700 mt-0.5 tabular-nums">
                  {typeSummary(yearConversionTypes)}
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto ios-momentum-scroll px-2 py-2">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-7 w-7 animate-spin text-gray-300" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 text-center py-8 px-2">{error}</p>
          ) : inDetail ? (
            detailLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-7 w-7 animate-spin text-gray-300" />
              </div>
            ) : monthDetail ? (
              <div className="h-[240px] w-full px-2 pb-2">
                <WebsiteVisitsLineChart
                  days={monthDetail.days}
                  mode={visitsMode}
                  showConversions={monthDetail.types.length > 0}
                  openDay={monthDetail.openDay}
                  maxTicks={10}
                />
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">Could not load this month.</p>
            )
          ) : months.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No data for this year.</p>
          ) : (
            <ul className="space-y-0.5">
              {months.map((month) => {
                const clickable = month.visits > 0 || month.unique > 0;
                return (
                  <li key={month.monthKey}>
                    <button
                      type="button"
                      onClick={() => clickable && setSelectedMonthKey(month.monthKey)}
                      disabled={!clickable}
                      className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                        month.isCurrentMonth
                          ? 'bg-blue-50/90 ring-1 ring-blue-100'
                          : 'hover:bg-gray-50'
                      } ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <span
                        className={`text-sm ${
                          month.isCurrentMonth
                            ? 'font-semibold text-blue-900'
                            : 'font-medium text-gray-800'
                        }`}
                      >
                        {month.label}
                        {month.isCurrentMonth ? (
                          <span className="ml-1.5 text-[10px] font-semibold uppercase text-blue-600">
                            This month
                          </span>
                        ) : null}
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        <span className="flex flex-col items-end">
                          <span
                            className={`text-sm tabular-nums ${
                              month.visits > 0
                                ? month.isCurrentMonth
                                  ? 'font-bold text-blue-900'
                                  : 'font-semibold text-gray-900'
                                : 'font-medium text-gray-400'
                            }`}
                          >
                            {month.visits.toLocaleString()} visits
                          </span>
                          <span className="text-[11px] font-medium text-gray-400 tabular-nums">
                            {month.unique.toLocaleString()} unique
                            {month.conversionRate != null
                              ? ` · ${month.conversionRate.toFixed(1)}%`
                              : ''}
                          </span>
                          {month.conversionTypes.length > 0 ? (
                            <span className="text-[11px] font-medium text-emerald-700 tabular-nums text-right">
                              {typeSummary(month.conversionTypes)}
                            </span>
                          ) : null}
                        </span>
                        {clickable ? (
                          <ChevronRight className="h-4 w-4 text-gray-300" />
                        ) : (
                          <span className="w-4" />
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
